import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Box, Stack, Divider } from '@mui/material'
import { CommonSelect, CommonAddress } from '@/components/common'
import {
  DateScroll,
  ResultCard,
  DonorPortalScreenMessage,
  SuggestionsSection,
} from '@/components/donation'
import {
  useGetOperationSchedules,
  useGetProcedureTypes,
  useGetOperationLocations,
} from '@/hooks'
import { useDonorContext } from '@/context'
import { ROUTES, DONOR_PORTAL_SCREEN_CONFIG_CODE } from '@/constants'
import { ResultCardSkeleton } from './Skeletons'
import {
  buildResultItem,
  getDonateNearYouParams,
  parseDonateNearYouParams,
  getScheduledDonationsParams,
  formatIsoDateWithWeekday,
  getDonorEligibilityForScheduleRow,
  isScheduleRowBookable,
} from '@/utils'

export const DonateNearYouResults = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { procedureTypes, loadingProcedureTypes, getProcedureTypes } =
    useGetProcedureTypes()
  const {
    operationSchedules,
    operationScheduleSuggestions,
    getOperationSchedules,
    loadingOperationSchedules,
  } = useGetOperationSchedules()
  const {
    operationLocationsMap,
    getOperationLocations,
    loadingOperationLocations,
  } = useGetOperationLocations()
  const { donor, getDonor } = useDonorContext()

  const lastValidAddressRef = useRef(null)
  const [formData, setFormData] = useState({})

  useEffect(() => {
    getProcedureTypes()
  }, [])

  useEffect(() => {
    getDonor()
  }, [getDonor])

  useEffect(() => {
    const parsedFormData = parseDonateNearYouParams(searchParams)
    setFormData(parsedFormData)

    // Store valid address
    if (
      parsedFormData.address?.formatted_address &&
      parsedFormData.address?.latitude &&
      parsedFormData.address?.longitude
    ) {
      lastValidAddressRef.current = parsedFormData.address
    }

    if (
      parsedFormData.address?.latitude &&
      parsedFormData.address?.longitude &&
      parsedFormData.date
    ) {
      getOperationSchedules(parsedFormData)
      getOperationLocations(parsedFormData)
    }
  }, [searchParams])

  const handleFormDataChange = (key, value) => {
    setFormData(prev => {
      // If address is cleared, restore last valid address
      if (key === 'address' && (!value || Object.keys(value).length === 0)) {
        if (lastValidAddressRef.current) {
          value = lastValidAddressRef.current
        }
      } else if (key === 'address' && value?.formatted_address) {
        // Store new valid address
        lastValidAddressRef.current = value
      }

      const updatedFormData = { ...prev, [key]: value }
      getOperationSchedules(updatedFormData)
      getOperationLocations(updatedFormData)

      const newParams = getDonateNearYouParams(updatedFormData)
      window.history.replaceState(
        null,
        '',
        `${ROUTES.DONATE_NEAR_YOU_RESULTS}?${newParams.toString()}`
      )

      return updatedFormData
    })
  }

  const handleSelectDate = useCallback(chip => {
    handleFormDataChange('date', chip.id)
  }, [])

  const handleScrollDate = useCallback(
    chip => {
      getOperationLocations({ ...formData, date: chip.id })
    },
    [formData]
  )

  const buildFilterAndSortResults = useCallback(
    (schedules, userLocation, maxDistance) => {
      if (!schedules || schedules.length === 0) return []

      const builtResults = schedules.map(schedule =>
        buildResultItem(schedule, {
          latitude: userLocation.latitude,
          longitude: userLocation.longitude,
        })
      )
      const filteredResults =
        maxDistance === 'any'
          ? builtResults
          : builtResults.filter(result => result.distance <= maxDistance)

      return filteredResults.sort((a, b) => a.distance - b.distance)
    },
    []
  )

  const builtResults = useMemo(
    () =>
      buildFilterAndSortResults(
        operationSchedules,
        formData.address,
        formData.distance
      ),
    [
      buildFilterAndSortResults,
      operationSchedules,
      formData.address,
      formData.distance,
    ]
  )

  const builtSuggestions = useMemo(() => {
    if (!operationScheduleSuggestions?.length || !formData.address) return []

    const filtered = buildFilterAndSortResults(
      operationScheduleSuggestions,
      formData.address,
      formData.distance
    )

    if (filtered.length > 0 || formData.distance === 'any') {
      return filtered
    }

    return buildFilterAndSortResults(
      operationScheduleSuggestions,
      formData.address,
      'any'
    )
  }, [
    buildFilterAndSortResults,
    operationScheduleSuggestions,
    formData.address,
    formData.distance,
  ])

  const handleSelectResult = useCallback(
    (resultItem, rawRow) => {
      if (!isScheduleRowBookable(rawRow)) return
      const appointmentDayIso = resultItem.date || formData.date
      const { applies, selectable } = getDonorEligibilityForScheduleRow({
        donorId: donor?.id,
        procedureEligibilities: donor?.procedure_eligibilities,
        selectedProcedureTypeId: formData.procedure_type_id,
        appointmentDayIso,
        procedureTypesOnRow: rawRow?.procedure_types,
      })
      if (applies && !selectable) return
      navigate(
        `${ROUTES.SCHEDULE_DONATION}?${getScheduledDonationsParams({
          ...resultItem,
          ...formData,
          date: appointmentDayIso,
        }).toString()}`
      )
    },
    [donor?.id, donor?.procedure_eligibilities, formData, navigate]
  )

  const renderScheduleResultCard = useCallback(
    (resultItem, rawRow, index, { operationDate } = {}) => {
      const appointmentDayIso = resultItem.date || formData.date
      const elig = getDonorEligibilityForScheduleRow({
        donorId: donor?.id,
        procedureEligibilities: donor?.procedure_eligibilities,
        selectedProcedureTypeId: formData.procedure_type_id,
        appointmentDayIso,
        procedureTypesOnRow: rawRow?.procedure_types,
      })
      const scheduleBlocked =
        !isScheduleRowBookable(rawRow) || (elig.applies && !elig.selectable)

      return (
        <ResultCard
          key={`${resultItem.operation_id}-${appointmentDayIso}-${index}`}
          item={resultItem}
          operationDate={
            operationDate ??
            (appointmentDayIso
              ? formatIsoDateWithWeekday(appointmentDayIso)
              : undefined)
          }
          onSelect={() => handleSelectResult(resultItem, rawRow)}
          isSelectDisabled={scheduleBlocked}
          selectionSurface='buttonOnly'
          eligibilityChip={elig.applies ? elig.chip : null}
        />
      )
    },
    [
      donor?.id,
      donor?.procedure_eligibilities,
      formData.procedure_type_id,
      formData.date,
      handleSelectResult,
    ]
  )

  return (
    <Stack spacing={2} sx={{ pb: 1 }}>
      <DonorPortalScreenMessage
        code={DONOR_PORTAL_SCREEN_CONFIG_CODE.DONATE_NEAR_YOU_RESULTS}
      />
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ alignItems: { sm: 'flex-end' } }}
      >
        <CommonAddress
          id='dny-filter-address'
          label='Address'
          placeholder='Enter a location'
          value={formData?.address}
          setValue={v => handleFormDataChange('address', v)}
        />
        <CommonSelect
          id='dny-filter-procedure'
          label='Donation Type'
          placeholder='All Available'
          options={procedureTypes}
          loading={loadingProcedureTypes}
          value={formData?.procedure_type_id}
          onChange={v => handleFormDataChange('procedure_type_id', v)}
        />
      </Stack>

      <Divider />

      <DateScroll
        id='dny-date-scroll'
        formData={formData}
        dataMap={operationLocationsMap}
        loadingDataMap={loadingOperationLocations}
        onSelect={handleSelectDate}
        onScroll={handleScrollDate}
      />

      <Divider />

      <Box sx={{ mt: 2 }}>
        <SuggestionsSection
          loading={loadingOperationSchedules}
          hasResults={builtResults.length > 0}
          hasSuggestions={builtSuggestions.length > 0}
          suggestionsSubtitle='Here are sessions on nearby dates in your area'
        >
          <Stack spacing={2}>
            {builtSuggestions.map((resultItem, index) =>
              renderScheduleResultCard(resultItem, resultItem, index)
            )}
          </Stack>
        </SuggestionsSection>
        <Stack spacing={2}>
          {loadingOperationSchedules
            ? Array.from({ length: 5 }).map((_, index) => (
                <ResultCardSkeleton key={`skeleton-${index}`} />
              ))
            : builtResults.map((resultItem, index) =>
                renderScheduleResultCard(resultItem, resultItem, index, {
                  operationDate: formData.date
                    ? formatIsoDateWithWeekday(formData.date)
                    : undefined,
                })
              )}
        </Stack>
      </Box>
    </Stack>
  )
}
